/**
 * ============================================================================
 * BUSINESS LOGIC (BL): GESTIÓN DE PLAYLIST Y HERRAMIENTAS DE CABINA DJ (Tile ID 46)
 * ============================================================================
 * Implementa validaciones de cliente, herramientas de cabina DJ (Spotify, YouTube,
 * Portapapeles, Exportación Excel), mapeos y control de flujo de la dinámica.
 */

import * as DLPlaylist from '../../../DL/Admin/Overlays/Playlist';
import { ValidateSqlInjection } from '../../Security';

// ============================================================================
// 1. HERRAMIENTAS DE CABINA DJ Y UTILIDADES DE CLIENTE
// ============================================================================

/**
 * Copia el título y artista de la pista al portapapeles para pegado rápido en software DJ.
 * @param {string} title - Título de la pista.
 * @param {string} [artist] - Nombre del artista opcional.
 * @returns {Promise<{success: boolean, text: string, message: string}>}
 */
export const CopyTrackToClipboard = async (title, artist = '') => {
    try {
        const cleanTitle = (title || '').trim();
        const cleanArtist = (artist || '').trim();

        if (!cleanTitle) {
            return { success: false, text: '', message: 'No hay título para copiar.' };
        }

        const trackText = cleanArtist ? `${cleanTitle} - ${cleanArtist}` : cleanTitle;

        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(trackText);
        } else {
            const textArea = document.createElement('textarea');
            textArea.value = trackText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
        }

        return { success: true, text: trackText, message: `Copiado: "${trackText}"` };
    } catch (error) {
        return { success: false, text: '', message: 'No fue posible copiar al portapapeles.' };
    }
};

/**
 * Abre una búsqueda directa de la canción o artista en Spotify Web.
 * @param {string} title - Título de la canción.
 * @param {string} [artist] - Artista.
 */
export const OpenSpotifySearch = (title, artist = '') => {
    const cleanTitle = (title || '').trim();
    const cleanArtist = (artist || '').trim();
    const query = cleanArtist ? `${cleanTitle} ${cleanArtist}` : cleanTitle;

    if (!query) return;

    const url = `https://open.spotify.com/search/${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Abre una búsqueda directa de la canción o artista en YouTube.
 * @param {string} title - Título de la canción.
 * @param {string} [artist] - Artista.
 */
export const OpenYouTubeSearch = (title, artist = '') => {
    const cleanTitle = (title || '').trim();
    const cleanArtist = (artist || '').trim();
    const query = cleanArtist ? `${cleanTitle} ${cleanArtist}` : cleanTitle;

    if (!query) return;

    const url = `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Formatea una fecha ISO a formato local legible con hora.
 * @param {string} dateString - Fecha ISO UTC.
 * @returns {string} Fecha formateada.
 */
export const FormatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        });
    } catch {
        return dateString;
    }
};

/**
 * Traduce el código de modo de pantalla a texto amigable.
 * @param {number|string} mode 
 * @returns {string}
 */
export const FormatScreenModeLabel = (mode) => {
    switch (Number(mode)) {
        case 0:
            return 'Llamado a la acción (QR + Título)';
        case 1:
            return 'Canción en reproducción (Now Playing)';
        case 2:
            return 'Top sugerencias / En cola';
        default:
            return 'Pantalla por defecto';
    }
};

// ============================================================================
// 2. LÓGICA DE NEGOCIO Y OPERACIONES DE PLAYLIST
// ============================================================================

/**
 * Registra una sugerencia de aficionado aplicando validaciones previas de seguridad.
 */
export const SubmitSuggestion = async (suggestionData) => {
    try {
        if (!suggestionData || !suggestionData.title || !suggestionData.title.trim()) {
            return { success: false, message: 'Escribe el nombre de la canción o artista.' };
        }

        const isTitleThreat = await ValidateSqlInjection(suggestionData.title);
        if (isTitleThreat) {
            return { success: false, message: 'El texto ingresado contiene caracteres no permitidos.' };
        }

        if (suggestionData.artistName) {
            const isArtistThreat = await ValidateSqlInjection(suggestionData.artistName);
            if (isArtistThreat) {
                return { success: false, message: 'El nombre del artista contiene caracteres no permitidos.' };
            }
        }

        return await DLPlaylist.SubmitSuggestion(suggestionData);
    } catch (error) {
        return { success: false, message: 'Ocurrió un error al procesar tu sugerencia.' };
    }
};

export const GetPublicConfig = async () => {
    return await DLPlaylist.GetPublicConfig();
};

export const GetSuggestions = async (options = {}) => {
    return await DLPlaylist.GetSuggestions(options);
};

export const CreateManualSong = async (manualData) => {
    try {
        if (!manualData || !manualData.title || !manualData.title.trim()) {
            return { success: false, message: 'El título de la pista es obligatorio.' };
        }

        const isThreat = await ValidateSqlInjection(manualData.title);
        if (isThreat) {
            return { success: false, message: 'El título contiene texto no permitido.' };
        }

        return await DLPlaylist.CreateManualSong(manualData);
    } catch (error) {
        return { success: false, message: 'Error al agregar canción en la cabina.' };
    }
};

export const ToggleFavorite = async (id, isFavorite = null) => {
    if (!id) return { success: false, message: 'ID no proporcionado.' };
    return await DLPlaylist.ToggleFavorite(id, isFavorite);
};

export const ToggleArchive = async (id, isArchive = null) => {
    if (!id) return { success: false, message: 'ID no proporcionado.' };
    return await DLPlaylist.ToggleArchive(id, isArchive);
};

export const UpdateSuggestion = async (id, updateData) => {
    if (!id) return { success: false, message: 'ID no proporcionado.' };
    if (!updateData || !updateData.title || !updateData.title.trim()) {
        return { success: false, message: 'El título no puede estar vacío.' };
    }
    return await DLPlaylist.UpdateSuggestion(id, updateData);
};

export const DeleteSuggestion = async (id) => {
    if (!id) return { success: false, message: 'ID no proporcionado.' };
    return await DLPlaylist.DeleteSuggestion(id);
};

export const GetActivePlaylist = async () => {
    return await DLPlaylist.GetActivePlaylist();
};

export const ReorderPlaylist = async (orderedIds) => {
    if (!Array.isArray(orderedIds) || orderedIds.length === 0) {
        return { success: false, message: 'Lista de ordenamiento vacía.' };
    }
    return await DLPlaylist.ReorderPlaylist(orderedIds);
};

export const SetNowPlaying = async (songId) => {
    return await DLPlaylist.SetNowPlaying(songId);
};

export const GetNowPlaying = async () => {
    return await DLPlaylist.GetNowPlaying();
};

export const TogglePlayed = async (id, isPlayed = null) => {
    if (!id) return { success: false, message: 'ID no proporcionado.' };
    return await DLPlaylist.TogglePlayed(id, isPlayed);
};

export const GetMetrics = async () => {
    return await DLPlaylist.GetMetrics();
};

export const GetSettings = async () => {
    return await DLPlaylist.GetSettings();
};

export const UpdateSettings = async (settingsData) => {
    if (!settingsData) return { success: false, message: 'Configuración inválida.' };
    return await DLPlaylist.UpdateSettings(settingsData);
};

export const TogglePause = async (isActive) => {
    return await DLPlaylist.TogglePause(isActive);
};

export const SetScreenMode = async (screenMode) => {
    return await DLPlaylist.SetScreenMode(screenMode);
};

export const ProyectarScreen = async (screenMode = 0) => {
    return await DLPlaylist.ProyectarScreen(screenMode);
};

export const ResetDynamic = async (confirmationPassword) => {
    if (!confirmationPassword || !confirmationPassword.trim()) {
        return { success: false, message: 'Debes ingresar la contraseña maestra de confirmación.' };
    }
    return await DLPlaylist.ResetDynamic(confirmationPassword);
};

export const ExportPlaylistToExcel = async () => {
    return await DLPlaylist.ExportToExcel();
};

/**
 * Valida y envía un asset multimedia para optimización con SkiaSharp en el backend.
 * @param {string} assetType - Tipo de asset (overlay-1536, overlay-1920, screen-bg, qr-logo)
 * @param {File} file - Archivo seleccionado
 * @param {boolean} [updateSettings=true] - Actualizar en base de datos inmediatamente
 */
export const UploadAsset = async (assetType, file, updateSettings = true) => {
    try {
        if (!file) {
            return { success: false, message: 'Selecciona una imagen válida.' };
        }

        if (!assetType || !assetType.trim()) {
            return { success: false, message: 'El tipo de asset es requerido.' };
        }

        const validMimes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
        if (!validMimes.includes(file.type.toLowerCase()) && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
            return {
                success: false,
                message: 'Formato no admitido. Solo se permiten imágenes JPEG, PNG o WebP.'
            };
        }

        const maxBytes = 25 * 1024 * 1024; // 25 MB
        if (file.size > maxBytes) {
            return {
                success: false,
                message: `El archivo (${(file.size / (1024 * 1024)).toFixed(1)} MB) supera el límite máximo de 25 MB.`
            };
        }

        return await DLPlaylist.UploadAsset(assetType, file, updateSettings);
    } catch (error) {
        return {
            success: false,
            message: 'Error al preparar el archivo para subir.'
        };
    }
};

export { DLPlaylist };
